import React from 'react';

import classNames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';

import WanderuErrorBoundary from 'wanderu-component-error-boundary';
import {
    i18nMomentPropType,
    localize,
} from 'wanderu-i18n';
import {
    range,
    generateFakeOnChangeEvent,
} from 'wanderu-utils';

import CalendarFooter from './CalendarFooter/CalendarFooter';
import Day from './Day/Day';
import DayHeader from './DayHeader/DayHeader';
import MonthHeader from './MonthHeader/MonthHeader';

import styles from './Calendar.scss';

const DAYS_IN_WEEK = 7;

function makeDateArrayForMonth(date) {
    const firstOfMonth = date?.clone()?.startOf('month');
    const numDaysBeforeFirstOfMonth = firstOfMonth?.weekday();
    const startDate = firstOfMonth?.clone()?.subtract(numDaysBeforeFirstOfMonth, 'days');
    const daysInMonth = firstOfMonth?.daysInMonth();
    const numberOfWeeks = (daysInMonth + numDaysBeforeFirstOfMonth) / DAYS_IN_WEEK;
    const totalDays = Math.ceil(numberOfWeeks) * DAYS_IN_WEEK;

    return range(totalDays).map((i) => startDate?.clone()?.add(i, 'day')?.format());
}

class Calendar extends React.Component {
    static propTypes = {
        calendarClass: PropTypes.string,
        dateData: PropTypes.object,
        dayHeaderClass: PropTypes.string,
        hideCalendar: PropTypes.func,
        highlightClass: PropTypes.string,
        i18nMoment: PropTypes.func,
        maxDate: i18nMomentPropType,
        minDate: i18nMomentPropType,
        onDateChange: PropTypes.func,
        selectedDate: i18nMomentPropType,
        today: PropTypes.string,
    };

    static defaultProps = {
        dateData: {},
        hideCalendar: () => undefined,
        selectedDate: moment(),
    };

    constructor(props) {
        super(props);
        let { selectedDate } = this.props;
        this.state = {
            dates: makeDateArrayForMonth(selectedDate),
            firstOfMonth: selectedDate?.clone()?.startOf('month')?.format(),
            lastOfMonth: selectedDate?.clone()?.endOf('month')?.format(),
        };
    }

    componentDidUpdate(prevProps) {
        if (!this.props.selectedDate?.isSame(prevProps.selectedDate, 'day')) {
            this.setCalendarTo(this.props.selectedDate, this.props.minDate);
        }
    }

    setCalendarTo(selectedDate, minDate) {
        let date = selectedDate;
        if (date?.isBefore(this.props.minDate, 'days')) {
            date = minDate;
        }

        this.setState({
            dates: makeDateArrayForMonth(date),
            firstOfMonth: date?.clone()?.startOf('month')?.format(),
            lastOfMonth: date?.clone()?.endOf('month')?.format(),
        });
    }

    onMonthChange = (numberOfMonths) => {
        const {
            i18nMoment,
        } = this.props;
        const {
            firstOfMonth,
        } = this.state;
        let newDate = i18nMoment(firstOfMonth)?.clone()?.add(numberOfMonths, 'month');

        this.setState({
            dates: makeDateArrayForMonth(newDate),
            firstOfMonth: newDate?.clone()?.startOf('month')?.format(),
            lastOfMonth: newDate?.clone()?.endOf('month')?.format(),
        });
    };

    onFooterClick = (e) => {
        const fakeChangeEvent = generateFakeOnChangeEvent(e, moment());

        this.props.onDateChange(fakeChangeEvent);
        this.props.hideCalendar();
    };

    onClick = (e) => {
        this.props.onDateChange(e);
        this.props.hideCalendar();
    };

    isBeforeMinDate(date) {
        if (!this.props.minDate) {
            return false;
        }
        return date?.isBefore(this.props.minDate, 'day');
    }

    isAfterMinDate(date) {
        if (!this.props.minDate) {
            return true;
        }
        return date?.isAfter(this.props.minDate, 'day');
    }

    isAfterMaxDate(date) {
        if (!this.props.maxDate) {
            return false;
        }
        return date?.isAfter(this.props.maxDate, 'day');
    }

    renderWeek = (weekIndex) => {
        return (
            <tr key={`week-${weekIndex}`}>
                {range(DAYS_IN_WEEK).map((dayOfWeek) => {
                    const dateIndex = weekIndex * DAYS_IN_WEEK + dayOfWeek;
                    return this.renderDay(dateIndex);
                })}
            </tr>
        );
    };

    renderDay(dateIndex) {
        const {
            i18nMoment,
            selectedDate,
            dateData,
            highlightClass,
        } = this.props;
        const {
            dates,
            firstOfMonth,
            lastOfMonth,
        } = this.state;
        const date = i18nMoment(dates[dateIndex]);
        const isInLastMonth = date?.isBefore(i18nMoment(firstOfMonth), 'day');
        const isInNextMonth = date?.isAfter(i18nMoment(lastOfMonth), 'day');
        const isNotThisMonth = isInLastMonth || isInNextMonth;
        const isDisabled = this.isBeforeMinDate(date) || this.isAfterMaxDate(date);
        const data = dateData
            ? dateData[date?.format('YYYY-MM-DD')]
            : null;

        return (
            <Day
                data={data}
                date={date}
                deemphasize={isNotThisMonth}
                disabled={isDisabled}
                highlightClass={highlightClass}
                highlighted={date?.isSame(selectedDate, 'day')}
                isToday={date?.isSame(moment(), 'day')}
                key={dateIndex}
                onClick={this.onClick}
            />
        );
    }

    render() {
        const {
            today,
            i18nMoment,
            calendarClass,
            dayHeaderClass,
        } = this.props;
        const {
            dates,
            firstOfMonth,
        } = this.state;
        const numberOfWeeksInGrid = parseInt(dates.length / DAYS_IN_WEEK, 10);
        const dayNames = range(DAYS_IN_WEEK).map((i) => i18nMoment(dates[i]).format('dd'));

        return (
            <WanderuErrorBoundary
                fallback=""
                scope="Calendar"
            >
                <div
                    className={classNames(
                        styles.container,
                        calendarClass,
                    )}
                    data-testid="calendar"
                >
                    <MonthHeader
                        currentMonth={i18nMoment(firstOfMonth)}
                        enablePreviousMonthButton={this.isAfterMinDate(i18nMoment(firstOfMonth))}
                        onMonthChange={this.onMonthChange}
                    />
                    <table className={styles.calendar}>
                        <tbody>
                            <DayHeader
                                className={dayHeaderClass}
                                dayNames={dayNames}
                            />
                            {range(numberOfWeeksInGrid).map(this.renderWeek)}
                        </tbody>
                    </table>
                    {today && (
                        <CalendarFooter
                            onClick={this.onFooterClick}
                            today={today}
                        />
                    )}
                </div>
            </WanderuErrorBoundary>
        );
    }
}

export { Calendar };
export default localize(Calendar);
